import {
  Box,
  Text,
  Divider,
  Flex,
  Tooltip,
  Icon,
  Collapse,
} from '@chakra-ui/react';
import { useState, useRef, useEffect, useMemo } from 'react';
import { CustomLockIcon } from '../../../theme/icons/CustomLockIcon';
import { CustomInfoOutlineIcon } from '../../../theme/icons/CustomInfoOutlineIcon';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useAppSelector } from '../../../store/redux/hooks';
import PlanUpgradeButton from './PlanUpgradeButton';
import { sectionHasErrorsForId } from './utils/sectionErrorMatching';

const SectionWrapper = ({
  children,
  title,
  titleBadge,
  subtitle,
  tooltip,
  locked,
  lockedMessage,
  showUpgradeCta = true,
  openOnLoad = false,
  autoOpenOnceWhen = false,
  id,
  selectedTab,
  bg: sectionBg,
  onClose,
}: {
  children: React.ReactNode;
  title: React.ReactNode;
  titleBadge?: React.ReactNode;
  subtitle?: React.ReactNode;
  tooltip?: React.ReactNode | string;
  locked?: boolean;
  lockedMessage?: React.ReactNode;
  showUpgradeCta?: boolean;
  required?: boolean;
  openOnLoad?: boolean;
  autoOpenOnceWhen?: boolean;
  id?: string;
  selectedTab?: string;
  bg?: string;
  onClose?: () => void;
}) => {
  const [isActive, setIsActive] = useState(false);
  const [isOpen, setIsOpen] = useState(openOnLoad);
  const sectionRef = useRef<HTMLDivElement>(null);
  const didAutoOpenFromSignalRef = useRef(false);
  const errors = useAppSelector((state) => state.templateErrors);

  const sectionHasErrors = useMemo(
    () => sectionHasErrorsForId(id, Object.keys(errors), selectedTab),
    [errors, id, selectedTab]
  );

  useEffect(() => {
    if (sectionHasErrors) {
      setIsOpen(true);
    }
  }, [sectionHasErrors]);

  useEffect(() => {
    if (!autoOpenOnceWhen) {
      didAutoOpenFromSignalRef.current = false;
      return;
    }
    if (!didAutoOpenFromSignalRef.current) {
      setIsOpen(true);
      didAutoOpenFromSignalRef.current = true;
    }
  }, [autoOpenOnceWhen]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const handleFocusIn = () => setIsActive(true);
    const handleFocusOut = (e: FocusEvent) => {
      if (!section.contains(e.relatedTarget as Node)) {
        setIsActive(false);
      }
    };
    const handleClick = (e: MouseEvent) => {
      const headerToggle = section.querySelector('[data-section-header]');
      if (headerToggle && headerToggle.contains(e.target as Node)) {
        return;
      }
      setIsActive(true);
    };
    const handleDocumentClick = (e: MouseEvent) => {
      if (!section.contains(e.target as Node)) {
        setIsActive(false);
      }
    };

    section.addEventListener('focusin', handleFocusIn);
    section.addEventListener('focusout', handleFocusOut);
    section.addEventListener('click', handleClick);
    document.addEventListener('click', handleDocumentClick);

    return () => {
      section.removeEventListener('focusin', handleFocusIn);
      section.removeEventListener('focusout', handleFocusOut);
      section.removeEventListener('click', handleClick);
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  return (
    <Box
      id={id}
      ref={sectionRef}
      position="relative"
      borderWidth="2px"
      borderStyle="solid"
      borderColor={isActive ? 'orangePrimary' : 'lightGrey'}
      borderRadius="10px"
      p={6}
      bg={sectionBg ?? 'white'}
      transition="border-color 0.2s ease"
      boxShadow={isActive ? '5px 10px 10px 0px #00000026' : 'none'}
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        cursor="pointer"
        onClick={() => {
          if (isOpen) onClose?.();
          setIsOpen(!isOpen);
        }}
        data-section-header
      >
        <Flex alignItems="center" gap={2} minW={0}>
          <Text as="span" fontSize="18px" fontWeight="600" lineHeight="1.2">
            {title}
          </Text>
          {tooltip && (
            <Tooltip
              label={tooltip}
              hasArrow
              placement="top"
              fontSize="12px"
              bg="inkBlue"
              color="white"
              borderRadius="6px"
              textAlign="center"
              p="10px"
            >
              <Box
                as="span"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                w="18px"
                h="18px"
              >
                <Icon
                  as={locked ? CustomLockIcon : CustomInfoOutlineIcon}
                  fill="grey"
                  boxSize="18px"
                  display="block"
                />
              </Box>
            </Tooltip>
          )}
          {titleBadge}
        </Flex>
        {isOpen ? (
          <ChevronUpIcon boxSize="26px" />
        ) : (
          <ChevronDownIcon boxSize="26px" />
        )}
      </Flex>

      {subtitle && (
        <Box mt={2} color="grey">
          {typeof subtitle === 'string' ? (
            <Text fontSize="15px">{subtitle}</Text>
          ) : (
            subtitle
          )}
        </Box>
      )}

      <Collapse in={isOpen} animateOpacity>
        <Divider borderColor="primaryBackground" mb={4} mt={4} />

        {locked ? (
          <>
            <Text as="div" fontSize="16px" color="grey" mb={6}>
              {lockedMessage ??
                'You need to upgrade plan to enable this feature.'}
            </Text>
            {showUpgradeCta && <PlanUpgradeButton />}
          </>
        ) : (
          children
        )}
      </Collapse>
    </Box>
  );
};

export default SectionWrapper;
